import { useGLTF } from "@react-three/drei";
import React from "react";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import { isUHandleCode } from "../../../../../app/providers/doorHandleProfiles";
import { getFullAssetPath } from "../../../../../shared/utils/assets";
import { DoorComponentProps } from "../../Door";
import Beam from "../components/Beam";
import Glass from "../components/Glass";
import JHandle from "../components/JHandle";
import UHandle from "../components/UHandle";
import Frame from "./Frame";
import { HandleSmalRond } from "./handles/HandleSmalRond";
import { DeIjsselLayout } from "../layouts/DeIjsselLayout";
import { DeMaasLayout } from "../layouts/DeMaasLayout";
import { DeWaalCutLayouts } from "../layouts/DeWaalCutLayouts";
import { DeWaalLayoutXLXXL } from "../layouts/DeWaalLayoutXLXXL";
import { TaatsGLTFResult } from "./TaatsType";
import { TaatsVerticalLayout } from "./layouts/TaatsVerticalLayout";
import { getTaatsLocalVerticalPosition } from "./layouts/taatsVerticalLayoutUtils";

interface TaatsProps extends DoorComponentProps {
  secondary?: boolean;
}

const Taats: React.FC<TaatsProps> = ({
  materials,
  position,
  rotation,
  index = 0,
  frameSettings,
  rotateNewlayouts = false,
  secondary = false,
}: TaatsProps) => {
  const { nodes } = useGLTF(
    getFullAssetPath("/objects-v3/taatsdeur_v6.glb"),
  ) as TaatsGLTFResult;

  const {
    handle,
    sceneStaanderPosition,
    getConfigurationHeight,
    getConfigurationBeams,
    getConfigurationWidth,
    getConfigurationColor,
    isDeMaasLayout,
    isDeWaalLayout,
    isDeIjsselLayout,
    rotateDeWaal23,
    rotateDeMaas2Dubbel,
    selectedDoor,
    isLeafStyleDeWaalLayout,
  } = useConfigurationOptionsContext();

  const showSmalRondHandle =
    handle?.code === "normalhandletaatsrond" ||
    handle?.code === "normalhandletaatsronddubbel";

  const chosenMaterial = materials[getConfigurationColor()];
  const shouldMirrorVerticalBeam = selectedDoor?.code === "dubbel";
  const localVerticalPosition = getTaatsLocalVerticalPosition(
    sceneStaanderPosition,
    shouldMirrorVerticalBeam,
  );
  const doorWidth = getConfigurationWidth(index);
  const doorHeight = getConfigurationHeight(index);
  const frameScaleX = frameSettings?.generalScaleX ?? 1;
  const frameScaleY = frameSettings?.generalScaleY ?? 1;

  const showHandleOnSecondary =
    !secondary ||
    handle?.code.includes("dubbel") ||
    selectedDoor?.code.includes("dubbel");
  const isVerticalHandle = isUHandleCode(handle?.code);
  const configuredBeams = getConfigurationBeams(!isVerticalHandle);

  return (
    <group
      scale-x={frameScaleX}
      dispose={null}
      position={position}
      rotation={rotation}
    >
      <group scale-y={frameScaleY} position-y={-0.007}>
        <Frame
          doorNodes={nodes}
          materials={materials}
          index={index}
          {...frameSettings}
        />
      </group>
      <Glass doorNodes={nodes} materials={materials} index={index} />
      <TaatsVerticalLayout
        nodes={nodes}
        material={chosenMaterial}
        width={doorWidth}
        height={doorHeight}
        localPosition={localVerticalPosition}
        frameSettings={frameSettings}
      />
      {isVerticalHandle ? (
        <UHandle
          reverse={frameSettings?.UHandleReverse ?? false}
          doorNodes={nodes}
          materials={materials}
          index={index}
          shiftXValue={frameSettings?.UHandleShiftX ?? 0}
        />
      ) : !showSmalRondHandle ? (
        showHandleOnSecondary ? (
          <group
            position-z={frameSettings?.JhandleGeneralShiftZ ?? 0}
            rotation-y={frameSettings?.JhandleGeneralRotationY ? Math.PI : 0}
          >
            <JHandle
              doorNodes={nodes}
              materials={materials}
              index={index}
              reverse={frameSettings?.JHandleReverse ?? false}
              shiftZValue={frameSettings?.JhandleShiftZ ?? 0}
              shiftXValue={frameSettings?.JhandleShiftX ?? 0}
            />
          </group>
        ) : null
      ) : null}

      <group rotation-y={rotateNewlayouts ? Math.PI : 0}>
        {showSmalRondHandle && showHandleOnSecondary && (
          <group>
            <HandleSmalRond
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          </group>
        )}
        {/* De Maas */}
        {isDeMaasLayout && (
          <group rotation-y={rotateDeMaas2Dubbel ? Math.PI : 0}>
            <DeMaasLayout
              nodes={nodes}
              chosenMaterial={chosenMaterial}
              doorIndex={index}
            />
          </group>
        )}

        {/* De Waal */}
        {isDeWaalLayout && (
          <group rotation-y={rotateDeWaal23 ? Math.PI : 0}>
            {isLeafStyleDeWaalLayout ? (
              <DeWaalCutLayouts
                nodes={nodes}
                chosenMaterial={chosenMaterial}
                doorIndex={index}
              />
            ) : (
              <DeWaalLayoutXLXXL
                nodes={nodes}
                chosenMaterial={chosenMaterial}
                doorIndex={index}
              />
            )}
          </group>
        )}

        {/* De Ijssel */}
        {isDeIjsselLayout && (
          <DeIjsselLayout
            nodes={nodes}
            chosenMaterial={chosenMaterial}
            doorIndex={index}
          />
        )}
      </group>
      {configuredBeams.map((beam, i) => {
        return (
          <Beam
            id={beam.id}
            key={`taats-beam-${i}`}
            doorNodes={nodes}
            materials={materials}
            width={doorWidth}
            height={beam.height / 1000}
          />
        );
      })}
    </group>
  );
};

export default Taats;
